/**
 * Pure search logic for the inline room picker.
 *
 * Extracted from RoomPickerInlineComponent so it can be unit-tested without
 * spinning up Angular/TestBed. The component is a thin shell that wires
 * signals to this function.
 *
 * The function mirrors the map-page search pattern (see SearchPanelComponent):
 *
 *   1. Always compute a local substring match against the already-loaded
 *      spaces map — instant, case-insensitive, robust to partial queries.
 *   2. If the debounced backend searchMap has returned hits, merge them on
 *      top (deduped by id). Even a low-score hit is useful if it brings in
 *      a room the local substring filter couldn't reach (e.g. matches via
 *      amenities, tags, or embeddings).
 *   3. Apply the same exclusion filters in both paths: must be a
 *      `meetingroom`, must be `bookable`, must be at the caller's location.
 *   4. Sort prefix matches first, then alphabetical.
 *   5. Cap at `limit` (default 20).
 *
 * Availability is annotated on every result from the caller-supplied
 * `getAvailability` lookup (typically backed by the reactive
 * PlatformDataService.availabilityMap signal, which RealtimeService keeps
 * fresh over WebSocket).
 */

export type RoomAvailabilityStatus = 'available' | 'busy' | 'unknown';

export interface RoomSearchableSpace {
  _id: string;
  name?: string;
  floorId?: string;
  spaceType?: string;
  bookable?: boolean;
  locationId?: string;
  image?: string;
  capacity?: number;
}

export interface RoomSearchApiHit {
  id: string;
  name: string;
  spaceType?: string;
}

export interface RoomSearchAvailabilityLookup {
  availableAtTime?: boolean;
  hasBooking?: boolean;
}

export interface RoomPickerOption {
  _id: string;
  name: string;
  floorId?: string;
  spaceType: string;
  availability: RoomAvailabilityStatus;
  availabilityLabel: string;
  image?: string;
  capacity?: number;
}

export interface RoomPickerSearchInput {
  query: string;
  primaryLocationId: string;
  spaces: Iterable<RoomSearchableSpace>;
  apiResults?: RoomSearchApiHit[] | null;
  /** Optional live lookup — return `undefined` for spaces with no availability data. */
  getAvailability?: (spaceId: string) => RoomSearchAvailabilityLookup | undefined;
  limit?: number;
}

function toOption(
  space: RoomSearchableSpace,
  getAvailability: RoomPickerSearchInput['getAvailability'],
): RoomPickerOption {
  const avail = getAvailability?.(space._id);
  let availability: RoomAvailabilityStatus = 'unknown';
  let availabilityLabel = 'Unknown';
  if (avail) {
    if (avail.availableAtTime) {
      availability = 'available';
      availabilityLabel = 'Available';
    } else {
      availability = 'busy';
      availabilityLabel = avail.hasBooking ? 'In use' : 'Unavailable';
    }
  }
  return {
    _id: space._id,
    name: space.name || 'Unnamed',
    floorId: space.floorId,
    spaceType: space.spaceType || 'meetingroom',
    availability,
    availabilityLabel,
    image: space.image,
    capacity: space.capacity,
  };
}

export function computeRoomPickerResults(
  input: RoomPickerSearchInput,
): RoomPickerOption[] {
  const trimmed = (input.query ?? '').trim();
  if (!trimmed) return [];
  if (!input.primaryLocationId) return [];

  const lowerQuery = trimmed.toLowerCase();
  const limit = input.limit ?? 20;
  const byId = new Map<string, RoomPickerOption>();

  // 1. Local substring match — source of truth for partial matches.
  for (const s of input.spaces) {
    if (s.spaceType?.toLowerCase() !== 'meetingroom') continue;
    if (s.bookable === false) continue;
    if (s.locationId && s.locationId !== input.primaryLocationId) continue;
    const name = (s.name || '').toLowerCase();
    if (!name.includes(lowerQuery)) continue;
    byId.set(s._id, toOption(s, input.getAvailability));
  }

  // 2. Merge backend search hits for longer queries, if any.
  if (trimmed.length >= 4 && input.apiResults?.length) {
    // Index local spaces for quick cross-checks.
    const localIndex = new Map<string, RoomSearchableSpace>();
    for (const s of input.spaces) localIndex.set(s._id, s);

    for (const hit of input.apiResults) {
      if (hit.spaceType && hit.spaceType.toLowerCase() !== 'meetingroom') continue;
      if (byId.has(hit.id)) continue;
      const local = localIndex.get(hit.id);
      if (local && local.bookable === false) continue;
      if (local?.locationId && local.locationId !== input.primaryLocationId) continue;
      byId.set(
        hit.id,
        toOption(
          {
            _id: hit.id,
            name: hit.name,
            floorId: local?.floorId,
            spaceType: hit.spaceType ?? 'meetingroom',
            locationId: local?.locationId,
            bookable: local?.bookable,
          },
          input.getAvailability,
        ),
      );
    }
  }

  const out = Array.from(byId.values());
  out.sort((a, b) => {
    const aStarts = a.name.toLowerCase().startsWith(lowerQuery) ? 0 : 1;
    const bStarts = b.name.toLowerCase().startsWith(lowerQuery) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    return a.name.localeCompare(b.name);
  });
  return out.slice(0, limit);
}
